'use client';

/**
 * MobileExplorerDrawer — slide-in left overlay that hosts the Explorer.
 *
 * On mobile, Explorer is not a persistent pane — canvas (and copilot) stay
 * on screen while the drawer slides in on top. This preserves the user's
 * context in the canvas: tapping an entity in the Explorer tree makes a
 * selection, the drawer auto-closes, and the Canvas reacts to the new
 * selection without the user losing their place.
 *
 * Close triggers:
 *   - Backdrop tap
 *   - Close (X) button
 *   - Escape key
 *   - Any change to graph selection or active tab (the auto-close after
 *     a tree selection that makes the drawer feel "one-shot")
 *
 * Deliberately lightweight: no focus trap, no portal, no radix Dialog.
 * For a Phase 1 overlay this is enough — when it grows we can port to a
 * shadcn Sheet. Animated with a CSS transform and opacity gate so the
 * mounted-but-closed state is paint-free and doesn't block pointer events.
 *
 * @component_role: Mobile Explorer Drawer
 * @component_type: Layout Overlay
 * @domain: Frontend Shell
 * @layer: presentation
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { Explorer } from '@/components/explorer/explorer';
import { cn } from '@/lib/utils/cn';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTabStore } from '@/lib/stores/tab-store';

export function MobileExplorerDrawer() {
  const activeOverlay = useLayoutStore((s) => s.mobileActiveOverlay);
  const setActiveOverlay = useLayoutStore((s) => s.setMobileActiveOverlay);
  const isOpen = activeOverlay === 'explorer';
  const close = () => setActiveOverlay(null);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const activeTabId = useTabStore((s) => s.activeTabId);

  // Auto-close when the user makes a selection. We skip the first render so
  // the effect doesn't race with the drawer opening.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (isOpen) close();
    // isOpen intentionally omitted — we only want to react to selection changes,
    // not to open/close transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, activeTabId]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'fixed inset-0 z-50',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      {/* Backdrop */}
      <div
        onClick={() => close()}
        className={cn(
          'absolute inset-0 bg-black/50 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Sliding panel */}
      <aside
        role="dialog"
        aria-label="Explorer"
        className={cn(
          'absolute inset-y-0 left-0 w-[85%] max-w-xs bg-sidebar border-r border-border',
          'flex flex-col shadow-xl transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="h-10 flex items-center px-3 border-b border-border bg-card">
          <span className="text-sm font-medium text-foreground">Explorer</span>
          <button
            type="button"
            onClick={() => close()}
            aria-label="Close explorer"
            className="ml-auto p-1 -mr-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
          <Explorer />
        </div>
      </aside>
    </div>
  );
}
